import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  ACCESS_TOKEN_NAME,
  USER_DETAIL,
  DOMAIN,
  TYPE,
  ADD_REGISTER,
  EDIT_REGISTER,
  VIEW_REGISTER,
  UPLOAD_FILES,
  CREATE_EXPEDIENT,
  SET_PATH_EXPEDIENT,
  DELETE_PATH_EXPEDIENT,
  VIEW_FILES,
  EDIT_FILE_DATA,
  MOVE_FILE,
  MOVE_FILE_TO_TRASH,
  SIGN_ELECTRONICALLY,
  NOTIFICATIONS,
  INTEGRATIONS,
  ROLES_PERMISSIONS,
  CANCEL_RECORD,
  ADD_WORKFLOW,
  VIEW_WORKFLOW,
  DESIGNER_FORMS,
  DESIGNER_FORMS_PERMISSIONS,
  PASSWORD_EXPIRATION_DAYS,
  WORKFLOW_FORMS_PERMISSIONS,
  WORKFLOW_ALL_FORMS,
  FIRST_EXECUTION_BOOLEAN,
  SUBMENU_EXPEDIENT_CONSULT_BOOLEAN,
  ADDITIONAL_SERVICES,
  USER_LANGUAGE,
} from "constants/securityConst";
import { getItemLocalStorage, setItemLocalStorage } from "utils/localStorage";
import { isNullOrUndefined } from "utils/validations";
import i18n from "i18next";

const UserContext = React.createContext(undefined);

export function UserContextProvider({ children }) {
  const [canNext, setCanNext] = useState(false);
  const [state, setState] = useState({
    loading: false,
    error: false,
    countError: 0,
  });

  const initializeState = (key) => {
    const storedValue = getItemLocalStorage(key);
    if (isNullOrUndefined(storedValue) === true) {
      return null;
    }
    return storedValue;
  };

  const [jwt, setJwt] = useState(() => initializeState(ACCESS_TOKEN_NAME));
  const [user, setUser] = useState(() => initializeState(USER_DETAIL));
  const [domain, setDomain] = useState(() => initializeState(DOMAIN));
  const [userType, setUserType] = useState(() => initializeState(TYPE));
  const [addRegister, setAddRegister] = useState(() => initializeState(ADD_REGISTER));
  const [editRegister, setEditRegister] = useState(() => initializeState(EDIT_REGISTER));
  const [viewRegister, setViewRegister] = useState(() => initializeState(VIEW_REGISTER));
  const [uploadFiles, setUploadFiles] = useState(() => initializeState(UPLOAD_FILES));
  const [createExpedient, setCreateExpedient] = useState(() => initializeState(CREATE_EXPEDIENT));
  const [setPathExpedient, setSetPathExpedient] = useState(() =>
    initializeState(SET_PATH_EXPEDIENT)
  );
  const [deletePathExpedient, setDeletePathExpedient] = useState(() =>
    initializeState(DELETE_PATH_EXPEDIENT)
  );
  const [viewFiles, setViewFiles] = useState(() => initializeState(VIEW_FILES));
  const [editFileData, setEditFileData] = useState(() => initializeState(EDIT_FILE_DATA));
  const [moveFile, setMoveFile] = useState(() => initializeState(MOVE_FILE));
  const [moveFileToTrash, setMoveFileToTrash] = useState(() => initializeState(MOVE_FILE_TO_TRASH));
  const [signElectronically, setSignElectronically] = useState(() =>
    initializeState(SIGN_ELECTRONICALLY)
  );
  const [notifications, setNotifications] = useState(() => initializeState(NOTIFICATIONS));
  const [integrationOptions, setIntegrationOptions] = useState(() => initializeState(INTEGRATIONS));
  const [rolesPermissionsGranted, setRolesPermissionsGranted] = useState(() =>
    initializeState(ROLES_PERMISSIONS)
  );
  const [cancelRecord, setCancelRecord] = useState(() => initializeState(CANCEL_RECORD));
  const [addWorkflow, setAddWorkflow] = useState(() => initializeState(ADD_WORKFLOW));
  const [viewWorkflow, setViewWorkflow] = useState(() => initializeState(VIEW_WORKFLOW));
  const [designerForms, setDesignerForms] = useState(() => initializeState(DESIGNER_FORMS));
  const [designerFormsPermissions, setDesignerFormsPermissions] = useState(() =>
    initializeState(DESIGNER_FORMS_PERMISSIONS)
  );
  const [passwordExpirationDays, setPasswordExpirationDays] = useState(() =>
    initializeState(PASSWORD_EXPIRATION_DAYS)
  );
  const [workflowAllForms, setWorkflowAllForms] = useState(() =>
    initializeState(WORKFLOW_ALL_FORMS)
  );
  const [workflowFormsPermissions, setWorkflowFormsPermissions] = useState(() =>
    initializeState(WORKFLOW_FORMS_PERMISSIONS)
  );
  const [firstExecution, setFirstExecution] = useState(() =>
    initializeState(FIRST_EXECUTION_BOOLEAN)
  );
  const [subMenuExpedientConsult, setSubMenuExpedientConsult] = useState(() =>
    initializeState(SUBMENU_EXPEDIENT_CONSULT_BOOLEAN)
  );
  const [additionalServices, setAdditionalServices] = useState(() =>
    initializeState(ADDITIONAL_SERVICES)
  );
  const [language, setLanguage] = useState(() => initializeState(USER_LANGUAGE));

  useEffect(() => {
    if (!isNullOrUndefined(firstExecution)) {
      setItemLocalStorage(FIRST_EXECUTION_BOOLEAN, firstExecution);
    }
  }, [firstExecution]);

  const resetStates = () => {
    setJwt(initializeState(ACCESS_TOKEN_NAME));
    setUser(initializeState(USER_DETAIL));
    setDomain(initializeState(DOMAIN));
    setUserType(initializeState(TYPE));
    setAddRegister(initializeState(ADD_REGISTER));
    setEditRegister(initializeState(EDIT_REGISTER));
    setViewRegister(initializeState(VIEW_REGISTER));
    setUploadFiles(initializeState(UPLOAD_FILES));
    setCreateExpedient(initializeState(CREATE_EXPEDIENT));
    setSetPathExpedient(initializeState(SET_PATH_EXPEDIENT));
    setDeletePathExpedient(initializeState(DELETE_PATH_EXPEDIENT));
    setViewFiles(initializeState(VIEW_FILES));
    setEditFileData(initializeState(EDIT_FILE_DATA));
    setMoveFile(initializeState(MOVE_FILE));
    setMoveFileToTrash(initializeState(MOVE_FILE_TO_TRASH));
    setSignElectronically(initializeState(SIGN_ELECTRONICALLY));
    setNotifications(initializeState(NOTIFICATIONS));
    setIntegrationOptions(initializeState(INTEGRATIONS));
    setRolesPermissionsGranted(initializeState(ROLES_PERMISSIONS));
    setCancelRecord(initializeState(CANCEL_RECORD));
    setAddWorkflow(initializeState(ADD_WORKFLOW));
    setViewWorkflow(initializeState(VIEW_WORKFLOW));
    setDesignerForms(initializeState(DESIGNER_FORMS));
    setDesignerFormsPermissions(initializeState(DESIGNER_FORMS_PERMISSIONS));
    setPasswordExpirationDays(initializeState(PASSWORD_EXPIRATION_DAYS));
    setWorkflowAllForms(initializeState(WORKFLOW_ALL_FORMS));
    setWorkflowFormsPermissions(initializeState(WORKFLOW_FORMS_PERMISSIONS));
    setFirstExecution(initializeState(FIRST_EXECUTION_BOOLEAN));
    setSubMenuExpedientConsult(initializeState(SUBMENU_EXPEDIENT_CONSULT_BOOLEAN));
    setAdditionalServices(initializeState(ADDITIONAL_SERVICES));
    setLanguage(initializeState(USER_LANGUAGE));
  };

  useEffect(() => {
    if (jwt) {
      resetStates();
    }
  }, [jwt]);

  useEffect(() => {
    if (!jwt || !language) return;
    i18n.changeLanguage(language);
    setItemLocalStorage(USER_LANGUAGE, language);
  }, [jwt, language]);

  return (
    <UserContext.Provider
      value={{
        canNext,
        setCanNext,
        state,
        setState,
        jwt,
        setJwt,
        user,
        setUser,
        domain,
        setDomain,
        userType,
        setUserType,
        addRegister,
        setAddRegister,
        editRegister,
        setEditRegister,
        viewRegister,
        setViewRegister,
        uploadFiles,
        setUploadFiles,
        createExpedient,
        setCreateExpedient,
        setPathExpedient,
        setSetPathExpedient,
        deletePathExpedient,
        setDeletePathExpedient,
        viewFiles,
        setViewFiles,
        editFileData,
        setEditFileData,
        moveFile,
        setMoveFile,
        moveFileToTrash,
        setMoveFileToTrash,
        signElectronically,
        setSignElectronically,
        notifications,
        setNotifications,
        integrationOptions,
        setIntegrationOptions,
        rolesPermissionsGranted,
        setRolesPermissionsGranted,
        cancelRecord,
        setCancelRecord,
        addWorkflow,
        setAddWorkflow,
        viewWorkflow,
        setViewWorkflow,
        designerForms,
        setDesignerForms,
        designerFormsPermissions,
        setDesignerFormsPermissions,
        passwordExpirationDays,
        setPasswordExpirationDays,
        workflowAllForms,
        setWorkflowAllForms,
        workflowFormsPermissions,
        setWorkflowFormsPermissions,
        firstExecution,
        setFirstExecution,
        subMenuExpedientConsult,
        setSubMenuExpedientConsult,
        additionalServices,
        setAdditionalServices,
        language,
        setLanguage,
      }}
    >
      {children}
    </UserContext.Provider>
  );
}

UserContextProvider.propTypes = {
  children: PropTypes.any.isRequired,
};

export default UserContext;
